import React, { useCallback, useState } from "react";
import Section from "../../../components/common/Section";
import ReceiverAddress from "./ReceiverAddress";
import { AddressOption, LocationState } from "../../../types/types";
import Input from "../../../components/Input";
import SelectInput from "../../../components/Select";
import FormErrorMessage from "../../../components/common/FormErrorMessage";
import {
  useFetchCountriesAllData,
  useFetchCountryCitiesAllData,
} from "../../../apis/locationApis";

interface ReceiverSectionProps {
  register: any;
  errors: any;
  setValue: any;
  watch: any;
  control: any;
  clearErrors: any;
  setError: any;
}

const ReceiverSection: React.FC<ReceiverSectionProps> = ({
  register,
  errors,
  setValue,
  watch,
  control,
  clearErrors,
  setError,
}) => {
  const [receiverLocation, setReceiverLocation] = useState<LocationState>({});
  const [selectedOption, setSelectedOption] = useState<AddressOption>("short");
  const [searchRef, setSearchRef] = useState<string>("");
  // API data hooks
  const { data: countries = [] } = useFetchCountriesAllData();
  const {
    data: cities = [],
    isPending: listPending,
    refetch: fetchReceiverCities,
  } = useFetchCountryCitiesAllData(
    receiverLocation?.country?.id,
    searchRef,
    selectedOption,
  );

  const handleCitySearch = (query: string) => {
    if (query?.trim().length >= 3) {
      setSearchRef(query);
      fetchReceiverCities(); // 🔹 trigger API
    }
  };

  // Handles country selection changes
  const handleCountryChange = useCallback(
    (val: string) => {
      const country = countries.find((c: any) => c.id === val);
      if (!country) return;

      setValue("receiver.dialCode", country?.dialCode);
      setValue("receiver.country", country.id);
      setValue("receiver.countryName", country.name);
      // Clear coordinates so map jumps to new country center (no stale pin)
      setValue("receiver.latitude", "");
      setValue("receiver.longitude", "");

      setReceiverLocation((prev) => ({
        ...prev,
        country,
        city: undefined,
        district: undefined,
      }));

      setValue("receiver.city", "");
      setValue("receiver.district", "");
      setValue("receiver.cityName", "");
      setValue("receiver.districtName", "");

      if (country.id) {
        fetchReceiverCities();
      }
    },
    [countries, setValue, fetchReceiverCities],
  );

  return (
    <Section title="RECEIVER INFORMATION">
      <div>
        <SelectInput
          name="receiver.country"
          className="col-span-2"
          label="Country"
          placeholder="Select Country"
          selectOptions={countries.map((country: any) => ({
            id: country.id,
            value: country.id,
            text: country.name,
          }))}
          register={register}
          clearErrors={clearErrors}
          validation={{ required: "Receiver country is required" }}
          error={errors.receiver?.country}
          disabled={!countries.length}
          value={receiverLocation?.country?.id}
          setValue={handleCountryChange}
        />
      </div>

      <div className="grid grid-cols-6 gap-2">
        {/* Contact Info */}
        <Input
          name="receiver.name"
          type="text"
          title="Name"
          register={register} // ← Just pass the function
          error={errors.receiver?.name}
          validation={{
            required: "Receiver name is required",
            pattern: {
              value: /^(?=.*[A-Za-z\u0600-\u06FF])[A-Za-z\u0600-\u06FF0-9\s-]+$/,
              message:
                "Name must include at least one letter (not only numbers)",
            },
            validate: (value) => {
              if (!value?.trim()) return "Name cannot be empty or only spaces";
              return true;
            },
          }}
          className="col-span-2"
        />
        {/* Mobile with Dial Code */}
        <div className="col-span-2">
          <label className="text-[#1E293B] text-sm whitespace-nowrap">
            Mobile<span className="text-red-600 ml-1">*</span>
          </label>
          <div
            className={`flex items-center rounded-full bg-white px-2 mt-1 border ${
              errors.receiver?.mobile ? "border-red-500" : "border-[#D8D8D8]"
            }`}
          >
            {/* Dial code box */}
            <span className="pr-1 text-gray-700 text-sm whitespace-nowrap">
              {watch("receiver.dialCode")
                ? `+${watch("receiver.dialCode")}`
                : "+--"}
            </span>
            <div className="flex-1 border-l-2">
              <Input
                name="receiver.mobile"
                type="number"
                removeDiv={true}
                register={register}
                error={errors.receiver?.mobile}
                hideError={true}
                validation={{
                  required: "Mobile number is required",
                  pattern: {
                    value: /^\d{9,10}$/,
                    message: "Mobile number must be 9 or 10 digits",
                  },
                }}
                other={{
                  maxLength: 10,
                  inputMode: "numeric",
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                    setValue("receiver.mobile", value, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  },
                }}
                inputClassName="w-full border-0 outline-none bg-transparent px-2 py-[6px] text-[#1E1E1E] hide-spinner"
              />
            </div>
          </div>
          {errors.receiver?.mobile && (
            <FormErrorMessage error={errors.receiver?.mobile} />
          )}
        </div>

        <Input
          name="receiver.email"
          type="text"
          title="Email"
          register={register}
          error={errors.receiver?.email}
          validation={{
            required: "Receiver email is required",
            setValueAs: (value: string) => value?.toLowerCase(),
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Please enter a valid email address",
            },
          }}
          className="col-span-2"
        />

        {/* Address Options */}
        <ReceiverAddress
          register={register}
          errors={errors}
          setValue={setValue}
          watch={watch}
          clearErrors={clearErrors}
          control={control}
          setError={setError}
          className="col-span-6"
          cities={cities}
          handleCitySearch={handleCitySearch}
          isCitiesFetchPending={listPending}
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
          countries={countries}
          onCountryChange={(countryId: string) => {
            handleCountryChange(countryId);
            const c = countries.find((x: any) => x.id === countryId);
            if (c) {
              setReceiverLocation((prev) => ({
                ...prev,
                country: c,
                city: undefined,
                district: undefined,
              }));
            }
          }}
        />
      </div>
    </Section>
  );
};

export default ReceiverSection;
